import { forwardRef, type PropsWithChildren } from 'react';

import classNames from 'classnames';

import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import type { IconType } from '../../icons/constants';
import Icon from '../../icons/icon';
import type { ButtonSize, ButtonVariant } from './types';

import styles from './button_toggle.module.css';

const sizeStyles: { [size in ButtonSize]: string } = {
  s: styles.size_s,
  m: styles.size_m,
};

const iconSizeStyles: { [size in ButtonSize]: string } = {
  s: styles.icon_size_s,
  m: styles.icon_size_m,
};

export type ButtonProps = {
  type?: 'submit' | 'reset' | 'button';
  className?: string;
  noBorder?: boolean;
  variant?: ButtonVariant;
  disabled?: boolean;
  wideBasis?: boolean;
  size?: ButtonSize;
  icon?: IconType;
  name?: string;
  value?: string | string[] | number;
  aria?: AriaProps;
  uppercase?: boolean;
  isToggled: boolean;
  onChange: (isToggled: boolean) => unknown;
};

const ButtonToggle = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(
  (
    {
      type = 'button',
      className,
      variant,
      disabled,
      wideBasis = true,
      size = 'm',
      icon,
      children,
      name,
      value,
      aria,
      uppercase,
      isToggled,
      onChange,
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        name={name}
        value={value}
        onClick={() => onChange(!isToggled)}
        className={classNames(
          className,
          styles.root,
          variant && styles[variant],
          sizeStyles[size],
          {
            [styles.wide_basis]: wideBasis,
            [styles.only_icon]: !children,
            [styles.is_toggled]: isToggled,
            [styles.uppercase]: uppercase,
          },
        )}
        {...ariaToReactProps(aria)}
      >
        {icon && (
          <Icon
            className={classNames(styles.icon, iconSizeStyles[size], {
              [styles.icon_offset]: Boolean(children),
            })}
            icon={icon}
            textAlign
          />
        )}
        {children}
      </button>
    );
  },
);

export default ButtonToggle;
